import React from 'react';
import styled, { css } from 'styled-components';
import { Link } from 'gatsby';
import LinkedIn from '../images/Icons/linkedin.svg';
import { breakpoints } from '../styles/screenSizes';

const LinkGrid = styled.div`
    width: max-content;
`;

const Heading = styled.h1`
    font-size: 16px;
    font-weight: 500;
    line-height: 18.96px;
    color: #fff;
    text-transform: uppercase;
    margin-bottom: 12px;

    @media screen and (max-width: 400px) {
        font-size: 14px;
        line-height: 16.41px;
    }

    @media screen and (max-width: 375px) {
        font-size: 13px;
    }
`;

const sharedLinkStyle = css`
    text-decoration: none;
    font-size: 14px;
    font-weight: 400;
    line-height: 31.92px;
    color: #d1d5db;
    display: block;
    ${({ address }) =>
        address &&
        css`
            line-height: 19.6px;
            margin-top: ${({ marginTop }) => marginTop};
        `};

    @media only screen and (max-width: 400px) {
        font-size: 13px;
        line-height: 28px;
    }

    @media only screen and (max-width: 375px) {
        font-size: 12px;
        line-height: 26px;
    }
`;

const NavLinks = styled.a`
    ${sharedLinkStyle}

    ${({ type }) =>
        type === 'contact' &&
        css`
            display: flex;
            align-items: end;
            justify-content: flex-end;
        `}
`;

const RedirectLink = styled(Link)`
    ${sharedLinkStyle}
`;

const FooterText = styled.p`
    ${sharedLinkStyle}
`;

const Lines = styled.div`
    border-top: 1px solid rgba(249, 250, 251, 0.5);
    margin: ${({ type }) => (type === 'contact' ? '0 0 24px 0' : '24px 0')};

    @media (max-width: ${breakpoints.sm}) {
        margin: 24px 0;
    }
`;

const GridContainer = styled.div`
    display: grid;
    grid-template-columns: 0.6fr 1fr 1fr;
    grid-column-gap: 64px;
    grid-auto-rows: auto;

    .contact_div {
        grid-column: span 2 / span 2;
        grid-row-start: 3;
        width: 100%;
        display: flex;
        flex-direction: column;

        .contact_details_container {
            display: flex;
            flex-direction: column;

            > div {
                display: flex;
                align-items: end;
                justify-content: space-between;
            }
        }
    }

    @media (max-width: ${breakpoints.sm}) {
        grid-column-gap: 24px;
    }
`;

const NavigationData = () => {
    return (
        <LinkGrid>
            <Heading>Navigation</Heading>
            {/* when accessing link from footer it will scroll to bottom of the page that is fixed by using gatsby Link instead of anchor tag */}
            <RedirectLink to="/">Home</RedirectLink>
            <RedirectLink to="/#solution">Solutions</RedirectLink>
            <RedirectLink to="/#pricing">Pricing</RedirectLink>
            <RedirectLink to="/#contact">Contact Us</RedirectLink>
            <Lines />
        </LinkGrid>
    );
};

const LinksData = () => {
    return (
        <LinkGrid>
            <Heading>Links</Heading>
            <RedirectLink target="_blank" rel="noreferrer" to="/terms-of-use">
                {' '}
                Terms of Use
            </RedirectLink>
            <RedirectLink target="_blank" rel="noreferrer" to="/privacy-policy">
                Privacy Policy
            </RedirectLink>
            <NavLinks
                target="_blank"
                rel="noreferrer"
                href="https://portal.authenticate.com/register?utm_source=universal&utm_medium=footer"
            >
                Get Started
            </NavLinks>
            <NavLinks
                target="_blank"
                rel="noreferrer"
                href="https://verify.authenticating.com/onboarding"
            >
                Medallion™
            </NavLinks>
            <NavLinks
                target="_blank"
                rel="noreferrer"
                href="https://apply.workable.com/authenticate"
            >
                Careers
            </NavLinks>
            <NavLinks
                target="_blank"
                rel="noreferrer"
                href="https://statuspage.freshping.io/48329-Authenticating"
            >
                System Status
            </NavLinks>
        </LinkGrid>
    );
};

const ContactData = () => {
    return (
        <>
            <Lines type="contact" />
            <Heading>Contact</Heading>
            <div className="contact_details_container">
                <div>
                    <FooterText marginTop="10px" address={true}>
                        Toll Free: <br /> (833) 2-VERIFY
                    </FooterText>
                    <NavLinks href="mailto:support@authenticate.com" address={true}>
                        Email: <br />
                        support@authenticate.com
                    </NavLinks>
                </div>
                <div>
                    <FooterText marginTop="10px" address={true}>
                        Address: <br />
                        730 Arizona Avenue, <br /> Suite 230, Santa Monica, CA 90401
                    </FooterText>
                    <NavLinks
                        href="https://www.linkedin.com/company/authenticatedotcom/"
                        target="_blank"
                        rel="noreferrer"
                        type="contact"
                    >
                        <img src={LinkedIn} alt="linkedIn-logo" />
                    </NavLinks>
                </div>
            </div>
        </>
    );
};

const ProductsData = () => {
    return (
        <LinkGrid>
            <Heading>Products</Heading>
            <RedirectLink to="/products/identity/">Identity Verification</RedirectLink>
            <RedirectLink to="/products/criminal-background-checks/">
                Criminal Background Checks
            </RedirectLink>
            <RedirectLink to="/products/background-check/">Background Verifications</RedirectLink>
            <RedirectLink to="/products/document-verification">Document Verification</RedirectLink>
            <RedirectLink to="/products/tcm/">True Continuous Monitoring™</RedirectLink>
            <RedirectLink to="/products/reauthenticate/">ReAuthenticate™</RedirectLink>
        </LinkGrid>
    );
};

const SolutionsData = () => {
    return (
        <LinkGrid>
            <Heading>Solutions</Heading>
            <RedirectLink to="/solutions/identity-fraud-prevention">
                Identity Fraud Prevention
            </RedirectLink>
            <RedirectLink to="/solutions/kyc-aml-compliance">KYC & AML Compliance</RedirectLink>
            <RedirectLink to="/solutions/guest-verification">Guest Verification</RedirectLink>
        </LinkGrid>
    );
};

const IndustriesData = () => {
    return (
        <LinkGrid>
            <Lines />
            <Heading>Industries</Heading>
            <RedirectLink to="/industries/finance">Financial Institutions</RedirectLink>
            <RedirectLink to="/industries/marketplaces">Marketplace</RedirectLink>
            <RedirectLink to="/industries/healthcare">Healthcare</RedirectLink>
        </LinkGrid>
    );
};

const WhitepaperData = () => {
    return (
        <LinkGrid>
            <Lines />
            <Heading>Whitepapers</Heading>
            <RedirectLink to="/whitepapers/finance">Financial Fraud</RedirectLink>
            <RedirectLink to="/whitepapers/marketplace">Marketplace Fraud</RedirectLink>
            <RedirectLink to="/whitepapers/healthcare">Healthcare Fraud</RedirectLink>
        </LinkGrid>
    );
};

const ResourceData = () => {
    return (
        <LinkGrid>
            <Lines />
            <Heading>Resources</Heading>
            <RedirectLink to="/resources/security-and-compliance">
                Security and Compliance
            </RedirectLink>
            <RedirectLink to="/resources/blog">Blog</RedirectLink>
            <NavLinks target="_blank" rel="noreferrer" href="https://info.authenticate.com/">
                Knowledge Base
            </NavLinks>
            <NavLinks target="_blank" rel="noreferrer" href="https://docs.authenticate.com/">
                Technical Documentation
            </NavLinks>
        </LinkGrid>
    );
};
export const DesktopFooterLayout = () => {
    return (
        <GridContainer>
            <div>
                <NavigationData />
                <LinksData />
            </div>
            <div>
                <ProductsData />
                <WhitepaperData />
            </div>
            <div>
                <SolutionsData />
                <IndustriesData />
                <ResourceData />
            </div>
            <div className="contact_div">
                <ContactData />
            </div>
        </GridContainer>
    );
};
export const MobileFooterLayout = () => {
    return (
        <GridContainer>
            <div>
                <NavigationData />
                <LinksData />
                <Lines />
                <SolutionsData />
            </div>

            <div>
                <ProductsData />
                <IndustriesData />
                <ResourceData />
            </div>

            <div className="contact_div">
                <ContactData isContact />
            </div>
        </GridContainer>
    );
};
